import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItem, ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type {
  IxcInventoryV1Balance,
  IxcInventoryV1CloseTradingDayTransaction,
  IxcInventoryV1CustomerOrder,
  IxcInventoryV1CustomerTrade,
  IxcInventoryV1Execution,
  IxcInventoryV1FirmOrderOutcome,
  IxcInventoryV1FullDayTotals,
  IxcInventoryV1InventoryTrade,
  IxcInventoryV1InventoryTradeTransaction,
  IxcInventoryV1RealizedGainLoss,
  IxcInventoryV1SettleTradingDayTransaction,
  IxcInventoryV1TradeDetail,
  IxcInventoryV1TradeTotals,
} from '@interxfi/types/generated/api';
import { getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getAssetDetailPath } from '../asset/asset-routing';
import { getRaglTransactionDetailPath } from '../clearing-ledger/transaction/ragl-transaction-routing';
import { getOrderMiddleMoneyListItemsWithSummary } from '../order/order-list-items';
import { getOrderDetailPath } from '../order/order-routing';
import { OrderSidePill } from '../order/order-side-pill';
import { getRegistrationAccountDetailPath } from '../registration/account/registration-account-routing';
import { getInventoryBooksOrderDetailPath } from './books-order/inventory-books-order-routing';
import { getInventoryFirmOrderDetailPath } from './firm-order/inventory-firm-order-routing';

export function getInventoryCustomerOrderListItems(data: IxcInventoryV1CustomerOrder | undefined, prefix = '') {
  return {
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    capacity: getReadonlyListItemTranslatedString(`${prefix}capacity`, 'Capacity', {
      namespace: 'ixcInventoryV1',
      enumName: 'IxcInventoryV1FirmOrderCapacity',
      value: data?.capacity,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryBalanceListItems(data: IxcInventoryV1Balance | undefined, prefix: string, labelPrefix: string) {
  return {
    quantity: getReadonlyListItemNumber(
      `${prefix}quantity`,
      [labelPrefix, 'Quantity'].filter(Boolean).join(' '),
      getNumericStringOrUndefinedFromNullable(data, 'quantity'),
    ),
    value: getReadonlyListItemCurrency(
      `${prefix}value`,
      [labelPrefix, 'Value'].filter(Boolean).join(' '),
      getNumericStringOrUndefinedFromNullable(data, 'value'),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

// Flat variant with prefixed labels ("Customer Buy Quantity", ...). Use on single-list
// surfaces (e.g. event drawers) where every label must stay unique. Grouped surfaces
// should use getInventoryTradeTotalsListGroups instead.
export function getInventoryTradeTotalsListItems(data: IxcInventoryV1TradeTotals | undefined, prefix = 'totals') {
  return {
    customerBuy: getInventoryBalanceListItems(data?.customerBuy, `${prefix}customerBuy`, 'Customer Buy'),
    customerSell: getInventoryBalanceListItems(data?.customerSell, `${prefix}customerSell`, 'Customer Sell'),
    inventoryBuy: getInventoryBalanceListItems(data?.inventoryBuy, `${prefix}inventoryBuy`, 'Inventory Buy'),
    inventorySell: getInventoryBalanceListItems(data?.inventorySell, `${prefix}inventorySell`, 'Inventory Sell'),
    risklessBuy: getInventoryBalanceListItems(data?.risklessBuy, `${prefix}risklessBuy`, 'Riskless Buy'),
    risklessSell: getInventoryBalanceListItems(data?.risklessSell, `${prefix}risklessSell`, 'Riskless Sell'),
  } as const satisfies ReadonlyListItemRecord;
}

// Grouped variant of getInventoryTradeTotalsListItems: the balance name becomes the group
// title and items keep plain "Quantity"/"Value" labels. Use where totals render as a
// ReadonlyListStack.
export function getInventoryTradeTotalsListGroups(data: IxcInventoryV1TradeTotals | undefined, prefix = 'totals'): ReadonlyListGroup[] {
  const getGroup = (id: string, title: string, balance: IxcInventoryV1Balance | undefined): ReadonlyListGroup => {
    const items = getInventoryBalanceListItems(balance, `${prefix}${id}`, '');

    return { id: `${prefix}${id}`, title, items: [items.quantity, items.value] };
  };

  return [
    getGroup('customerBuy', 'Customer Buy', data?.customerBuy),
    getGroup('customerSell', 'Customer Sell', data?.customerSell),
    getGroup('inventoryBuy', 'Inventory Buy', data?.inventoryBuy),
    getGroup('inventorySell', 'Inventory Sell', data?.inventorySell),
    getGroup('risklessBuy', 'Riskless Buy', data?.risklessBuy),
    getGroup('risklessSell', 'Riskless Sell', data?.risklessSell),
  ];
}

export function getInventoryRealizedGainLossListItems(data: IxcInventoryV1RealizedGainLoss | undefined, prefix = '') {
  return {
    quantitySold: getReadonlyListItemNumber(`${prefix}quantitySold`, 'Quantity Sold', getNumericStringOrUndefinedFromNullable(data, 'quantitySold')),
    cost: getReadonlyListItemCurrency(`${prefix}cost`, 'Cost', getNumericStringOrUndefinedFromNullable(data, 'cost')),
    proceeds: getReadonlyListItemCurrency(`${prefix}proceeds`, 'Proceeds', getNumericStringOrUndefinedFromNullable(data, 'proceeds')),
    gain: getReadonlyListItemCurrency(`${prefix}gain`, 'Gain', getNumericStringOrUndefinedFromNullable(data, 'gain')),
    transactionId: getReadonlyListItemId(
      `${prefix}transactionId`,
      'Transaction ID',
      data?.transactionId,
      getRaglTransactionDetailPath(data?.transactionId),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryDailyTotalsListItems(data: IxcInventoryV1FullDayTotals | undefined, prefix = '') {
  return {
    opening: getInventoryBalanceListItems(data?.opening, `${prefix}opening`, 'Opening'),
    netTrade: getInventoryBalanceListItems(data?.netTrade, `${prefix}netTrade`, 'Net Trade'),
    closing: getInventoryBalanceListItems(data?.closing, `${prefix}closing`, 'Closing'),
    realized: getInventoryRealizedGainLossListItems(data?.realized, `${prefix}realized`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryExecutionListItems(data: IxcInventoryV1Execution | undefined, prefix = '') {
  return {
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    executionTime: getReadonlyListItemDate(`${prefix}executionTime`, 'Execution Time', data?.executionTime, 'timestamp_iso_ms'),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    price: getReadonlyListItemCurrency(`${prefix}price`, 'Price', data?.price),
    priceSource: getReadonlyListItemTranslatedString(`${prefix}priceSource`, 'Price Source', {
      namespace: 'ixcInventoryV1',
      enumName: 'IxcInventoryV1PriceSource',
      value: data?.priceSource,
    }),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
  } as const satisfies ReadonlyListItemRecord;
}

// The ordered execution fields shared by every execution detail group and the
// executed/mixed-capacity summary rows. Kept here so the field set stays in one
// place across the books-order, firm-order, and books-order-detail surfaces.
export function getInventoryExecutionEventListItems(execution: ReturnType<typeof getInventoryExecutionListItems>): ReadonlyListItem[] {
  return [
    execution.executionId,
    execution.executionTime,
    execution.tradeDate,
    execution.settlementDate,
    execution.quantity,
    execution.price,
    execution.priceSource,
    execution.amount,
  ];
}

export function getInventoryFirmOrderOutcomeListItems(data: IxcInventoryV1FirmOrderOutcome | undefined, prefix = '') {
  return {
    averagePrice: getReadonlyListItemCurrency(`${prefix}averagePrice`, 'Average Price', data?.averagePrice),
    totalQuantity: getReadonlyListItemNumber(`${prefix}totalQuantity`, 'Total Quantity', data?.totalQuantity),
    outcome: getReadonlyListItemTranslatedString(`${prefix}outcome`, 'Outcome', {
      namespace: 'ixcInventoryV1',
      enumName: 'IxcInventoryV1FirmOrderOutcomeOutcome',
      value: data?.outcome,
    }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryTradeDetailListItems(data: IxcInventoryV1TradeDetail | undefined, prefix = '') {
  return {
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : null),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    fillPrice: getReadonlyListItemCurrency(`${prefix}fillPrice`, 'Fill Price', data?.fillPrice),
    grossAmount: getReadonlyListItemCurrency(`${prefix}grossAmount`, 'Gross Amount', data?.grossAmount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryTradeListItems(data: IxcInventoryV1InventoryTrade | undefined, prefix = '') {
  return {
    transactionId: getReadonlyListItemId(`${prefix}transactionId`, 'Transaction ID', data?.transactionId),
    firmOrderId: getReadonlyListItemId(
      `${prefix}firmOrderId`,
      'Firm Order ID',
      data?.firmOrderId,
      getInventoryFirmOrderDetailPath(data?.firmOrderId),
    ),
    trade: getInventoryTradeDetailListItems(data?.trade, `${prefix}trade.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryCustomerTradeListItems(data: IxcInventoryV1CustomerTrade | undefined, prefix = '') {
  return {
    customerOrderId: getReadonlyListItemId(
      `${prefix}customerOrderId`,
      'Customer Order ID',
      data?.customerOrderId,
      getOrderDetailPath(data?.customerOrderId),
    ),
    trade: getInventoryTradeDetailListItems(data?.trade, `${prefix}trade.`),
    booksOrderId: getReadonlyListItemId(
      `${prefix}booksOrderId`,
      'Books Order ID',
      data?.booksOrderId,
      getInventoryBooksOrderDetailPath(data?.booksOrderId),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryTradeTransactionListItems(data: IxcInventoryV1InventoryTradeTransaction | undefined, prefix = '') {
  return {
    firmOrderId: getReadonlyListItemId(
      `${prefix}firmOrderId`,
      'Firm Order ID',
      data?.firmOrderId,
      getInventoryFirmOrderDetailPath(data?.firmOrderId),
    ),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    trade: getInventoryTradeDetailListItems(data?.trade, `${prefix}trade.`),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    netAmount: getReadonlyListItemCurrency(`${prefix}netAmount`, 'Net Amount', data?.netAmount),
    ...getOrderMiddleMoneyListItemsWithSummary(data?.middleMoney, `${prefix}middleMoney.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventorySettleTradingDayTransactionListItems(data: IxcInventoryV1SettleTradingDayTransaction | undefined, prefix = '') {
  return {
    tradingDate: getReadonlyListItemDate(`${prefix}tradingDate`, 'Trading Date', data?.tradingDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    tradeTotals: getInventoryTradeTotalsListItems(data?.tradeTotals, `${prefix}tradeTotals.`),
    dailyTotals: getInventoryDailyTotalsListItems(data?.totals && 'full' in data.totals ? data.totals.full : undefined, `${prefix}dailyTotals.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getInventoryCloseTradingDayTransactionListItems(data: IxcInventoryV1CloseTradingDayTransaction | undefined, prefix = '') {
  return {
    tradingDate: getReadonlyListItemDate(`${prefix}tradingDate`, 'Trading Date', data?.tradingDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    tradeTotals: getInventoryTradeTotalsListItems(data?.tradeTotals, `${prefix}tradeTotals.`),
    dailyTotals: getInventoryDailyTotalsListItems(data?.dailyTotals, `${prefix}dailyTotals.`),
  } as const satisfies ReadonlyListItemRecord;
}
